import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Checkbox } from '@sravni/react-design-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import { CardsContext } from '../../../../../../context';
import { Rows, Row, Col, TextInput, DateInput, onlyRussianOutput } from '../../../../../../../landing.common';
import { FORM_FIELDS, MASKS, PAGE_CONTEXT, PLACEHOLDERS } from '../../../../../../../../constants';
import { FormAddressSuggest } from '../../../../../../../form/ui/suggest/address';
import { IUnitCodeSuggest, UnitCodeSuggest } from '../../../../../../../form/ui/suggest/unit.code';
import { IssuedSuggest } from '../../../../../../../form/ui/suggest/issued';
import { useFormData, useFormStore } from '../../../../../../../../store/selectors/formStore';
import { extendFormData, setPageContext } from '../../../../../../../../store/reducers/formStore';
import { useAnalytics, EventType } from '../../../../../../../hooks/analytics';
import { useFormOnBlur } from '../../../../../../../form/hooks/formHooks';
import { onComplete } from './onComplete';
import { schema } from './schema';

type Props = {
    onNext: () => void;
    onAutoValidationError: (e: ValidationError) => void;
};

/** Личные и паспортные данные */
export const Step2: React.FC<Props> = (props) => {
    const { onNext, onAutoValidationError } = props;
    const dispatch = useDispatch();
    const formData = useFormData();
    const { autoRun } = useFormStore();
    const { pushEvent } = useAnalytics();

    const { taskManager } = useContext(CardsContext);

    const form = useForm({ resolver: yupResolver(schema()), mode: 'onSubmit', reValidateMode: 'onBlur' });

    const {
        control,
        handleSubmit,
        /** dirtyFields are needed for keepDirtyValues */
        formState: { errors, dirtyFields: _, isValidating },
        reset,
        trigger,
        setValue,
        watch,
    } = form;

    const [issuedOptions, setIssuedOptions] = useState<Array<string>>([]);

    const inProcess = isValidating;

    const [addressesAreEqual] = watch([FORM_FIELDS.RESIDENCE_SAME_REGISTRATION]);

    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        dispatch(setPageContext(PAGE_CONTEXT.STEP_2));
        if (autoRun) {
            schema().validate(formData).then(onSubmit).catch(onAutoValidationError);
        }
    }, [autoRun]);

    const onSubmit = async () => {
        taskManager.register(onComplete());
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Шаг 2|Паспорт',
        });
        onNext();
    };

    const onUnitCodeBlur = async (
        event: FE.FieldChangeEvent,
        onBlur: (event: FE.FieldChangeEvent) => void,
        option?: IUnitCodeSuggest,
        optionsForAllCodes?: Array<IUnitCodeSuggest>,
    ) => {
        if (option && optionsForAllCodes) {
            const options = optionsForAllCodes.filter((item) => item.code === option.code);
            const mappedOptions = new Map(options.map((item) => [item.name, item.name]));
            setIssuedOptions(Array.from(mappedOptions.values()));
            setValue(FORM_FIELDS.ISSUED, options?.length === 1 ? options[0].name : '');
        } else if (!(await trigger(FORM_FIELDS.UNIT_CODE))) {
            setIssuedOptions([]);
            setValue(FORM_FIELDS.ISSUED, '');
        }

        onBlur(event);
    };

    const onSelectRegistrationAddress: React.ComponentProps<typeof FormAddressSuggest>['onSelect'] = useCallback(
        (option) => {
            dispatch(
                extendFormData({
                    [FORM_FIELDS.REGISTRATION_DADATA_FULL]: option,
                }),
            );
        },
        [],
    );

    const onSelectResidenceAddress: React.ComponentProps<typeof FormAddressSuggest>['onSelect'] = useCallback(
        (option) => {
            dispatch(
                extendFormData({
                    [FORM_FIELDS.RESIDENCE_DADATA_FULL]: option,
                }),
            );
        },
        [],
    );

    const formOnBlur = useFormOnBlur(form, false);

    return (
        <form onSubmit={handleSubmit(onSubmit)} onBlur={formOnBlur}>
            <Rows>
                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.BIRTHDAY}
                            render={({ field }) => (
                                <DateInput
                                    {...field}
                                    label="Дата рождения"
                                    errorMessage={errors[FORM_FIELDS.BIRTHDAY]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.BIRTH_PLACE}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Место рождения"
                                    errorMessage={errors[FORM_FIELDS.BIRTH_PLACE]?.message}
                                    onChange={onlyRussianOutput(field)}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.PASSPORT_NUMBER}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Серия и номер паспорта"
                                    mask={MASKS.PASSPORT}
                                    placeholder={PLACEHOLDERS.PASSPORT}
                                    errorMessage={errors[FORM_FIELDS.PASSPORT_NUMBER]?.message}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.PASSPORT_DATE}
                            render={({ field }) => (
                                <DateInput
                                    {...field}
                                    label="Когда выдан"
                                    errorMessage={errors[FORM_FIELDS.PASSPORT_DATE]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.UNIT_CODE}
                            render={({ field: { onBlur, ...field } }) => (
                                <UnitCodeSuggest
                                    input={{
                                        ...field,
                                        label: 'Код подразделения',
                                        errorMessage: errors[FORM_FIELDS.UNIT_CODE]?.message,
                                        mask: MASKS.UNIT_CODE,
                                    }}
                                    onBlur={(event, option, allOptions) =>
                                        onUnitCodeBlur(event, onBlur, option, allOptions)
                                    }
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.ISSUED}
                            render={({ field }) => (
                                <IssuedSuggest
                                    input={{
                                        ...field,
                                        label: 'Кем выдан',
                                        errorMessage: errors[FORM_FIELDS.ISSUED]?.message,
                                    }}
                                    options={issuedOptions}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-9>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.REGISTRATION_STREET}
                            render={({ field: { onBlur, ...field } }) => (
                                <FormAddressSuggest
                                    input={{
                                        ...field,
                                        label: 'Адрес регистрации',
                                        errorMessage: errors[FORM_FIELDS.REGISTRATION_STREET]?.message,
                                    }}
                                    onChange={field.onChange}
                                    onSelect={onSelectRegistrationAddress}
                                    onBlur={onBlur}
                                    toBound="house"
                                />
                            )}
                        />
                    </Col>
                    <Col md-3>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.REGISTRATION_APARTMENT}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    onChange={onlyRussianOutput(field)}
                                    label="Квартира"
                                    errorMessage={errors[FORM_FIELDS.REGISTRATION_APARTMENT]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.REGISTRATION_DATE}
                            render={({ field }) => (
                                <DateInput
                                    {...field}
                                    label="Дата регистрации"
                                    errorMessage={errors[FORM_FIELDS.REGISTRATION_DATE]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.RESIDENCE_SAME_REGISTRATION}
                            render={({ field: { value, ...field } }) => (
                                <Checkbox checked={value} {...field}>
                                    Адрес фактического проживания совпадает с адресом регистрации
                                </Checkbox>
                            )}
                        />
                    </Col>
                </Row>

                {!addressesAreEqual && (
                    <Row>
                        <Col md-9>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.RESIDENCE_STREET}
                                render={({ field: { onBlur, ...field } }) => (
                                    <FormAddressSuggest
                                        input={{
                                            ...field,
                                            label: 'Адрес фактического проживания',
                                            errorMessage: errors[FORM_FIELDS.RESIDENCE_STREET]?.message,
                                        }}
                                        onChange={field.onChange}
                                        onSelect={onSelectResidenceAddress}
                                        onBlur={onBlur}
                                        toBound="house"
                                    />
                                )}
                            />
                        </Col>
                        <Col md-3>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.RESIDENCE_APARTMENT}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        label="Квартира"
                                        errorMessage={errors[FORM_FIELDS.RESIDENCE_APARTMENT]?.message}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col md-8 />
                    <Col md-4>
                        <Button loading={inProcess} block variant="primary" type="submit">
                            Продолжить
                        </Button>
                    </Col>
                </Row>
            </Rows>
        </form>
    );
};
